import React, { useEffect, useState } from 'react';
import { ChevronDown, ChevronUp, ImagePlus, Trash2 } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { updateProperty } from '../../services/dataService';
import { uploadPropertyBuildingImage } from '../../services/propertyMediaService';
import { compressImageFileForUpload } from '../../utils/imageCompression';
import { isLikelyImageFile } from '../../utils/imageFileTypes';
import { getBuildingImageGallery } from '../../utils/propertyImages';

const MAX_PHOTOS = 12;

export default function BuildingPhotosSection({ property, onChange, readOnly = false }) {
  const { t } = useLanguage();
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [successHint, setSuccessHint] = useState(null);
  const [thumbLoadError, setThumbLoadError] = useState({});

  const urls = getBuildingImageGallery(property);

  useEffect(() => {
    setThumbLoadError({});
  }, [urls.join('|')]);

  useEffect(() => {
    if (!successHint) return undefined;
    const id = window.setTimeout(() => setSuccessHint(null), 8000);
    return () => window.clearTimeout(id);
  }, [successHint]);

  const persistUrls = async (nextUrls) => {
    const clean = nextUrls.filter((u) => typeof u === 'string' && /^https?:\/\//i.test(u.trim()));
    await updateProperty(property.id, {
      buildingImageUrls: clean,
      buildingImageUrl: clean[0] || '',
    });
    onChange((prev) => ({
      ...prev,
      buildingImageUrls: clean,
      buildingImageUrl: clean[0] || '',
    }));
  };

  const handleFiles = async (e) => {
    const input = e.target;
    if (!input || readOnly || !property.id) return;
    // `input.files` est une FileList *vivante* : si on fait input.value = '' avant,
    // la liste peut déjà être vide au test suivant (Edge/Chromium) → sortie silencieuse.
    const raw = input.files;
    if (!raw?.length) return;
    const picked = Array.from(raw);
    input.value = '';
    const remaining = MAX_PHOTOS - urls.length;
    if (remaining <= 0) {
      setError(t('basicInfo.buildingPhotosMax'));
      return;
    }
    setError(null);
    setSuccessHint(null);
    setUploading(true);
    try {
      const files = picked.slice(0, remaining);
      const newUrls = [...urls];
      let uploadedCount = 0;
      for (const file of files) {
        if (!isLikelyImageFile(file)) continue;
        const compressed = await compressImageFileForUpload(file);
        const url = await uploadPropertyBuildingImage(property.id, compressed);
        newUrls.push(url);
        uploadedCount += 1;
      }
      if (uploadedCount === 0 && files.length > 0) {
        setError(t('basicInfo.buildingPhotosNoValidImages'));
        return;
      }
      if (uploadedCount > 0) {
        await persistUrls(newUrls);
        setSuccessHint(
          t('basicInfo.buildingPhotosUploadSuccess').replace('{count}', String(uploadedCount)),
        );
      }
    } catch (err) {
      console.error(err);
      setError(t('basicInfo.buildingPhotoError'));
    } finally {
      setUploading(false);
    }
  };

  const removeAt = async (index) => {
    if (readOnly || !property.id) return;
    const next = urls.filter((_, i) => i !== index);
    await persistUrls(next);
  };

  const moveUrl = async (fromIndex, toIndex) => {
    if (readOnly || !property.id || toIndex < 0 || toIndex >= urls.length) return;
    const next = [...urls];
    const [item] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, item);
    await persistUrls(next);
  };

  return (
    <div className="border rounded-lg p-4 sm:p-6">
      <h2 className="text-lg font-semibold mb-2 text-blue-600 flex items-center gap-2">
        <ImagePlus className="w-5 h-5 shrink-0" aria-hidden />
        {t('basicInfo.buildingPhotosTitle')}
      </h2>
      <p className="text-sm text-gray-600 mb-2">{t('basicInfo.buildingPhotosHint')}</p>
      <p className="text-xs text-gray-500 mb-4">{t('basicInfo.buildingPhotosWhereUsed')}</p>

      {successHint ? (
        <div
          className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-900"
          role="status"
        >
          {successHint}
        </div>
      ) : null}

      {urls.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 mb-4">
          {urls.map((url, index) => (
            <div
              key={`${url}-${index}`}
              className="relative group rounded-lg border border-gray-200 overflow-hidden bg-gray-50 aspect-[4/3]"
            >
              {thumbLoadError[index] ? (
                <div className="w-full h-full flex flex-col items-center justify-center p-2 text-center text-xs text-amber-900 bg-amber-50">
                  {t('basicInfo.buildingPhotosThumbError')}
                </div>
              ) : (
                <img
                  src={url}
                  alt=""
                  className="w-full h-full object-cover"
                  loading="lazy"
                  referrerPolicy="no-referrer"
                  onError={() => setThumbLoadError((prev) => ({ ...prev, [index]: true }))}
                />
              )}
              {!readOnly && (
                <>
                  <div className="absolute top-1 left-1 flex flex-col gap-0.5">
                    <button
                      type="button"
                      onClick={() => moveUrl(index, index - 1)}
                      disabled={index === 0 || uploading}
                      className="p-1 rounded-md bg-white/95 text-gray-800 shadow border border-gray-200 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                      title={t('basicInfo.buildingPhotosReorderUp')}
                      aria-label={t('basicInfo.buildingPhotosReorderUp')}
                    >
                      <ChevronUp className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => moveUrl(index, index + 1)}
                      disabled={index === urls.length - 1 || uploading}
                      className="p-1 rounded-md bg-white/95 text-gray-800 shadow border border-gray-200 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                      title={t('basicInfo.buildingPhotosReorderDown')}
                      aria-label={t('basicInfo.buildingPhotosReorderDown')}
                    >
                      <ChevronDown className="w-4 h-4" />
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeAt(index)}
                    disabled={uploading}
                    className="absolute top-1 right-1 p-1.5 rounded-md bg-red-600 text-white opacity-90 hover:bg-red-700 shadow disabled:opacity-50"
                    title={t('basicInfo.buildingPhotoRemove')}
                    aria-label={t('basicInfo.buildingPhotoRemove')}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </>
              )}
              {index === 0 && (
                <span className="absolute bottom-1 left-1 px-1.5 py-0.5 text-[10px] font-medium bg-black/60 text-white rounded">
                  {t('basicInfo.buildingPhotoCover')}
                </span>
              )}
            </div>
          ))}
        </div>
      )}

      {!readOnly && (
        <>
          <label
            className={`relative inline-flex min-h-[2.5rem] min-w-[12rem] cursor-pointer items-center justify-center rounded-lg border border-blue-600 bg-blue-50 px-4 py-2 text-sm font-medium text-blue-800 hover:bg-blue-100 ${
              uploading || urls.length >= MAX_PHOTOS ? 'pointer-events-none opacity-50' : ''
            }`}
          >
            <span className="pointer-events-none relative z-0">{t('basicInfo.buildingPhotosChooseButton')}</span>
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif,.jpg,.jpeg,.png,.webp,.gif"
              multiple
              onChange={handleFiles}
              disabled={uploading || urls.length >= MAX_PHOTOS}
              className="absolute inset-0 z-10 h-full w-full cursor-pointer opacity-0"
            />
          </label>
          <p className="text-xs text-gray-500">{t('basicInfo.buildingPhotosChooseHint')}</p>
          {uploading ? <p className="text-xs text-gray-500 mt-2">{t('loading')}…</p> : null}
          {error ? <p className="text-xs text-red-600 mt-2">{error}</p> : null}
        </>
      )}
    </div>
  );
}
